/** Error boundary with a support reference (never a token or internal id) and a way out (§1.2 no dead ends). */
import { Button, ErrorState } from '@windrop/ui';
import { Component, type ErrorInfo, type ReactNode } from 'react';

let counter = 0;
/** `WDC-<time36>-<n>`: quotable by an operator, correlatable with console logs, free of secrets. */
export const newSupportReference = (): string => {
  counter += 1;
  return `WDC-${Date.now().toString(36).toUpperCase()}-${counter}`;
};

interface Props {
  children: ReactNode;
  /** Scope label used in the title ("this page", "the wizard"). */
  scope?: string;
  onReset?: () => void;
}

interface State {
  error: Error | null;
  reference: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, reference: null };

  static getDerivedStateFromError(error: Error): State {
    return { error, reference: newSupportReference() };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(`[console] ${this.state.reference ?? 'WDC'}`, error, info.componentStack);
  }

  override render() {
    if (!this.state.error) return this.props.children;
    return (
      <ErrorState
        title={`Something went wrong in ${this.props.scope ?? 'this page'}`}
        body="The rest of the console still works. Quote the reference below if you contact support."
        reference={this.state.reference ?? undefined}
        action={
          <Button
            onClick={() => {
              this.setState({ error: null, reference: null });
              this.props.onReset?.();
            }}
          >
            Try again
          </Button>
        }
        secondaryAction={
          <Button variant="secondary" href="/">
            Go to dashboard
          </Button>
        }
      />
    );
  }
}
